import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { CalendarProposal, DocumentProposal, ImprovementProposal, OutboxMessage, SSEEvent } from '../api/types.js';
import { useImprovements } from '../hooks/use-improvements.js';
import { useOutboxMessages } from '../hooks/use-messages.js';
import { ProposalCard } from './improvement-proposals.js';
import { MessageCard } from './outbox-messages.js';
import { useCalendarProposals } from '../hooks/use-calendar-proposals.js';
import { useDocumentProposals } from '../hooks/use-document-proposals.js';
import { useSlackProposals } from '../hooks/use-slack-proposals.js';
import { CalendarProposalCard } from './calendar-changes.js';
import { DocumentProposalCard } from './document-changes.js';
import { pairProposals, SlackProposalCard } from './slack-proposal-card.js';

// What a main session proposed, decided in that session (ADR-039 8c, step 10): its Slack messages, calendar changes,
// Drive changes, messages to another server and improvements to aidev (operator, 30/10/2026), each on its card after the tool call that made it. A proposal whose call is not among the events read
// is shown below the timeline, still decided here.

const tool = (e: SSEEvent) => (String(e.data.type) === 'tool_use' ? String(e.data.tool ?? '') : '');
const input = (e: SSEEvent) => (e.data.input ?? {}) as Record<string, unknown>;
const CALENDAR_ACTION: Record<string, CalendarProposal['action']> = { calendar_create_event: 'create', calendar_update_event: 'update', calendar_delete_event: 'delete' };

/** The calendar call a proposal came from: the same action, and the event's title (a new one) or id */
export function calendarCallMatches(e: SSEEvent, p: CalendarProposal): boolean {
  const action = CALENDAR_ACTION[tool(e).split('__').pop() ?? ''];
  if (!action || action !== p.action) return false;
  const i = input(e);
  return action === 'create' ? String(i.summary ?? '').trim() === String(p.change?.summary ?? '').trim() : String(i.eventId ?? '') === p.eventId;
}

/** The Drive call a proposal came from: the same file and kind of change */
export function driveCallMatches(e: SSEEvent, p: DocumentProposal): boolean {
  if (tool(e).split('__').pop() !== 'drive_propose') return false;
  const i = input(e);
  return String(i.fileId ?? '') === p.fileId && (i.change as { kind?: unknown } | undefined)?.kind === p.change.kind;
}

/** The improvement call a proposal came from: its title */
export function improvementCallMatches(e: SSEEvent, p: ImprovementProposal): boolean {
  return tool(e).split('__').pop() === 'propose_aidev_improvement' && String(input(e).title ?? '').trim() === p.title.trim();
}

/** The call a message to another server came from: the same server and text */
export function messageCallMatches(e: SSEEvent, m: OutboxMessage): boolean {
  if (tool(e).split('__').pop() !== 'send_message') return false;
  const i = input(e);
  return String(i.server ?? '') === m.to && String(i.text ?? '') === m.text;
}

/** Each call paired with the earliest proposal of the session it matches, not yet paired */
export function pairCalls<T>(events: readonly SSEEvent[], proposals: readonly T[], matches: (e: SSEEvent, p: T) => boolean): { byEvent: Map<number, T>; unpaired: T[] } {
  const left = [...proposals];
  const byEvent = new Map<number, T>();
  for (const e of events) {
    const i = left.findIndex((p) => matches(e, p));
    if (i >= 0) byEvent.set(e.id, left.splice(i, 1)[0]!);
  }
  return { byEvent, unpaired: left };
}

const byTime = <T extends { createdAt: string }>(a: T, b: T) => a.createdAt.localeCompare(b.createdAt);

/** The card after each event that made a proposal, and the cards of the proposals whose call was not read */
export function useSessionProposalCards(serverId: string, sessionId: string, events: readonly SSEEvent[] | undefined, isAdmin: boolean): {
  cardAfter: (event: SSEEvent) => ReactNode; unpaired: ReactNode[];
} {
  const all = useMemo(() => events ?? [], [events]);
  const { data: slack } = useSlackProposals(serverId, sessionId, all.length);
  // Decided here: its card stays below the timeline with the outcome, even when its call is not among the events read
  const [decidedHere, setDecidedHere] = useState<Set<string>>(() => new Set());
  const markDecided = useCallback((id: string) => setDecidedHere((prev) => new Set(prev).add(id)), []);
  const { data: calendar, dataUpdatedAt } = useCalendarProposals(serverId);
  const { data: documents } = useDocumentProposals(serverId);
  const { data: improvements } = useImprovements(serverId);
  const { data: messages } = useOutboxMessages(serverId);
  const paired = useMemo(() => ({
    slack: pairProposals(all, slack ?? []),
    calendar: pairCalls(all, (calendar ?? []).filter((p) => p.requestedBySession === sessionId).sort(byTime), calendarCallMatches),
    documents: pairCalls(all, (documents ?? []).filter((p) => p.requestedBySession === sessionId).sort(byTime), driveCallMatches),
    improvements: pairCalls(all, (improvements ?? []).filter((p) => p.requestedBySession === sessionId).sort(byTime), improvementCallMatches),
    messages: pairCalls(all, (messages ?? []).filter((m) => m.requestedBySession === sessionId).sort(byTime), messageCallMatches),
  }), [all, slack, calendar, documents, improvements, messages, sessionId]);
  const calendarCard = useCallback((p: CalendarProposal) => (
    <div key={p.proposalId} className="mx-4 my-1 max-w-[72ch]">
      <CalendarProposalCard serverId={serverId} p={p} isAdmin={isAdmin} focused={false} now={dataUpdatedAt} inSession onDecided={markDecided} />
    </div>
  ), [serverId, isAdmin, dataUpdatedAt, markDecided]);
  const documentCard = useCallback((p: DocumentProposal) => (
    <div key={p.proposalId} className="mx-4 my-1 max-w-[72ch]">
      <DocumentProposalCard serverId={serverId} p={p} isAdmin={isAdmin} focused={false} inSession onDecided={markDecided} />
    </div>
  ), [serverId, isAdmin, markDecided]);
  const improvementCard = useCallback((p: ImprovementProposal) => (
    <div key={p.id} className="mx-4 my-1 max-w-[72ch]">
      <ProposalCard serverId={serverId} p={p} isAdmin={isAdmin} focused={false} inSession onDecided={markDecided} />
    </div>
  ), [serverId, isAdmin, markDecided]);
  const messageCard = useCallback((m: OutboxMessage) => (
    <div key={m.id} className="mx-4 my-1 max-w-[72ch]">
      <MessageCard serverId={serverId} m={m} isAdmin={isAdmin} focused={false} inSession onDecided={markDecided} />
    </div>
  ), [serverId, isAdmin, markDecided]);
  const cardAfter = useCallback((event: SSEEvent) => {
    const s = paired.slack.byEvent.get(event.id);
    if (s) return <SlackProposalCard serverId={serverId} sessionId={sessionId} proposal={s} isAdmin={isAdmin} />;
    const c = paired.calendar.byEvent.get(event.id);
    if (c) return calendarCard(c);
    const d = paired.documents.byEvent.get(event.id);
    if (d) return documentCard(d);
    const i = paired.improvements.byEvent.get(event.id);
    if (i) return improvementCard(i);
    const m = paired.messages.byEvent.get(event.id);
    return m ? messageCard(m) : null;
  }, [paired, serverId, sessionId, isAdmin, calendarCard, documentCard, improvementCard, messageCard]);
  // Below the timeline, only what still waits: a decided proposal whose call is not read is the server page's history
  const unpaired = [
    ...paired.slack.unpaired.filter((p) => p.status === 'pending' || p.status === 'failed' || p.status === 'sending')
      .map((p) => <SlackProposalCard key={p.id} serverId={serverId} sessionId={sessionId} proposal={p} isAdmin={isAdmin} />),
    ...paired.calendar.unpaired.filter((p) => p.status === 'pending' || p.status === 'applying' || decidedHere.has(p.proposalId)).map(calendarCard),
    ...paired.documents.unpaired.filter((p) => p.status === 'pending' || p.status === 'applying' || p.status === 'failed' || decidedHere.has(p.proposalId)).map(documentCard),
    ...paired.improvements.unpaired.filter((p) => p.status === 'pending' || p.status === 'opening' || p.status === 'failed' || decidedHere.has(p.id)).map(improvementCard),
    ...paired.messages.unpaired.filter((m) => m.status === 'pending' || m.status === 'sending' || m.status === 'failed' || decidedHere.has(m.id)).map(messageCard),
  ];
  return { cardAfter, unpaired };
}
